import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface NavigatorStandalone extends Navigator {
  standalone?: boolean;
}

interface WindowWithMSStream extends Window {
  MSStream?: unknown;
}

const DISMISSED_KEY = 'pwa-install-dismissed';

export const PWAInstallPrompt = () => {
  const [t] = useTranslation('common');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Already installed as PWA — don't show prompt
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as NavigatorStandalone).standalone === true;
    setIsStandalone(standalone);
    if (standalone) {
      return;
    }

    if (localStorage.getItem(DISMISSED_KEY)) {
      return;
    }

    const iOS =
      (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !(window as WindowWithMSStream).MSStream;
    setIsIOS(iOS);

    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);

    // Chrome/Edge on Android/Desktop — native install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS — show manual instruction (Safari Add to Home Screen)
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 1500);
    }

    // Android without native prompt (some browsers) — show generic instruction
    if (android) {
      setTimeout(() => {
        if (!deferredPrompt) {
          setShowPrompt(true);
        }
      }, 2000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  if (isStandalone || !showPrompt) {
    return null;
  }

  const getMessage = () => {
    if (isIOS) {
      return t('installPWAiOS');
    }
    if (isAndroid && !deferredPrompt) {
      return t('installPWAAndroid');
    }
    return t('installPWA');
  };

  return (
    <div className='fixed inset-x-0 bottom-0 z-[200] bg-secondary-steel-blue p-4 shadow-lg'>
      <div className='mx-auto flex max-w-lg items-center justify-between gap-4'>
        <div className='flex-1 text-sm text-white'>{getMessage()}</div>
        <div className='flex shrink-0 items-center gap-2'>
          {!isIOS && deferredPrompt && (
            <Button
              type='button'
              variant={ButtonVariant.TERTIARY}
              handleOnClick={handleInstall}
              className='rounded-md bg-white px-4 py-2 text-sm font-semibold text-secondary-steel-blue hover:bg-blue-50'>
              {t('install')}
            </Button>
          )}
          <Button
            type='button'
            variant={ButtonVariant.TERTIARY}
            handleOnClick={handleDismiss}
            className='rounded-md border border-white px-4 py-2 text-sm font-medium text-white hover:bg-white/10'>
            {t('dismiss')}
          </Button>
        </div>
      </div>
    </div>
  );
};
